/* Twilio connection card for the Settings page.
 *
 * Multi-tenant BYOT: user pastes their own SID + Auth Token + From-number,
 * we validate against Twilio's API, then persist. Same UX shape as the
 * DocuSign card so users get muscle memory.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MessageSquare, Loader2, CheckCircle2, ExternalLink, Trash2, KeyRound, Eye, EyeOff } from "lucide-react";

export default function TwilioCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", from_number: "" });

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/twilio/status");
        if (alive) setStatus(data);
      } catch {
        if (alive) setStatus({ connected: false });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const connect = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/twilio/connect", form);
      setStatus(data);
      setOpen(false);
      setForm({ account_sid: "", auth_token: "", from_number: "" });
      toast.success(`Twilio connected — sending from ${data.from_number}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Twilio? Outbound SMS will stop working until you reconnect.")) return;
    setBusy(true);
    try {
      await api.delete("/twilio/disconnect");
      setStatus({ connected: false });
      toast.success("Twilio disconnected");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Disconnect failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[var(--line)] p-5">
        <Loader2 size={16} className="animate-spin inline mr-2" /> Loading Twilio…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-[var(--line)] p-5" data-testid="twilio-card">
      <div className="flex items-start justify-between mb-3 gap-3">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-50 border border-red-200 flex items-center justify-center shrink-0">
            <MessageSquare size={18} className="text-red-600" />
          </div>
          <div className="min-w-0">
            <div className="font-display font-extrabold text-lg leading-tight">Twilio SMS</div>
            <div className="text-xs text-[var(--ink-2)] mt-0.5">
              Send and receive text messages from inside iSpeedToClose using your own Twilio account.
            </div>
          </div>
        </div>
        {status?.connected && (
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 shrink-0">
            <CheckCircle2 size={10} /> Connected
          </span>
        )}
      </div>

      {status?.connected ? (
        <div className="space-y-2 mt-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">Account</div>
              <div className="font-mono text-xs mt-0.5 truncate">{status.account_friendly_name || "—"}</div>
              <div className="font-mono text-[10px] text-[var(--ink-2)] truncate">{status.account_sid}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[var(--ink-2)] font-bold">From-number</div>
              <div className="font-mono text-sm mt-0.5">{status.from_number}</div>
            </div>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-[var(--line)] mt-3">
            <div className="text-[11px] text-[var(--ink-2)]">
              SMS billing is on your Twilio account — we never charge per message.
            </div>
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy} data-testid="twilio-disconnect">
              <Trash2 size={12} className="mr-1.5" /> Disconnect
            </Button>
          </div>
        </div>
      ) : open ? (
        <div className="space-y-3 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">Account SID</Label>
            <Input
              className="font-mono text-xs"
              placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={form.account_sid}
              onChange={(e) => setForm((f) => ({ ...f, account_sid: e.target.value.trim() }))}
              data-testid="twilio-sid"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">Auth Token</Label>
            <div className="relative">
              <Input
                className="font-mono text-xs pr-9"
                type={showToken ? "text" : "password"}
                placeholder="Reveal on the Twilio Console dashboard"
                value={form.auth_token}
                onChange={(e) => setForm((f) => ({ ...f, auth_token: e.target.value.trim() }))}
                data-testid="twilio-token"
              />
              <button
                type="button"
                onClick={() => setShowToken((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--ink-2)] hover:text-[var(--ink)]"
                title={showToken ? "Hide" : "Show"}
              >
                {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="text-[10px] text-[var(--ink-2)] mt-1 italic">
              Tip: create a revocable <span className="font-semibold">API Key</span> on Twilio Console → API Keys instead of using your master Auth Token.
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-[var(--ink-2)] font-bold">From-Number</Label>
            <Input
              className="font-mono text-sm"
              placeholder="+15551234567"
              value={form.from_number}
              onChange={(e) => setForm((f) => ({ ...f, from_number: e.target.value.trim() }))}
              data-testid="twilio-from"
            />
            <div className="text-[10px] text-[var(--ink-2)] mt-1 italic">
              Must be a Twilio phone number you already own (Phone Numbers → Manage → Active Numbers).
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={() => setOpen(false)} disabled={busy} data-testid="twilio-cancel-btn">
              Cancel
            </Button>
            <button
              type="button"
              onClick={connect}
              disabled={busy || !form.account_sid || !form.auth_token || !form.from_number}
              data-testid="twilio-connect-submit"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-bold transition disabled:bg-emerald-300 disabled:text-white disabled:cursor-not-allowed"
            >
              {busy ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Connecting…
                </>
              ) : (
                <>
                  <KeyRound size={14} /> Connect Twilio
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-4">
          <Button
            onClick={() => setOpen(true)}
            className="w-full"
            data-testid="twilio-connect-btn"
          >
            <KeyRound size={14} className="mr-1.5" /> Connect Twilio
          </Button>
          <a
            href="https://console.twilio.com"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-[var(--ink-2)] hover:text-emerald-600 mt-2"
          >
            Get an Account SID + Auth Token from the Twilio Console <ExternalLink size={10} />
          </a>
        </div>
      )}
    </div>
  );
}
